// src/pages/Expenses.jsx
// Main page component for expense management with modern design

import React, { useState, useEffect } from 'react';
import ExpenseModal from './ExpenseModal';
import EditExpenseModal from './EditExpenseModal';
import ExpenseTable from './ExpenseTable';
import expenseService from '../../services/expenseService';
import { showNotification, alertNotification } from '../../utils/notification';
import { PlusCircle, Download } from 'lucide-react';
import './Expenses.css';

const Expenses = ({toggleSidebar}) => {
  // State management
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');

  // Real-time search with debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, filterType]);

  // Fetch expenses when page or filters change
  useEffect(() => {
    fetchExpenses();
  }, [currentPage, searchTerm, filterType]);

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterType('');
    setCurrentPage(1);
  };
  
  /**
   * Fetch all expenses from API
   */
  const fetchExpenses = async () => {
    setIsLoading(true);
    try {
      const data = await expenseService.getAll(currentPage, itemsPerPage, searchTerm, filterType);
      setExpenses(data);
      setHasNextPage(data.length === itemsPerPage);
      
      // Estimate total pages
      if (data.length < itemsPerPage) {
        setTotalPages(currentPage);
      } else {
        setTotalPages(currentPage + 1);
      }
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
      alertNotification('Failed to load expenses', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle creating new expenses (batch)
   */
  const handleCreateExpenses = async (payload) => {
    setIsSaving(true);
    try {
      await expenseService.create(payload);
      showNotification('Expenses created successfully', 'success');
      setIsAddModalOpen(false);
      setCurrentPage(1);
      fetchExpenses();
    } catch (error) {
      console.error('Failed to create expenses:', error);
      alertNotification('Failed to create expenses', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle updating an expense
   */
  const handleUpdateExpense = async (data) => {
    if (!selectedExpense) return;
    
    setIsSaving(true);
    try {
      const expenseId = selectedExpense.expenseId || selectedExpense.id;
      await expenseService.update(expenseId, data);
      showNotification('Expense updated successfully', 'success');
      setIsEditModalOpen(false);
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      console.error('Failed to update expense:', error);
      alertNotification('Failed to update expense', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle deleting an expense
   */
  const handleDeleteExpense = async (expense) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this ${expense.type} expense?`
    );
    
    if (!confirmed) return;
    
    try {
      const expenseId = expense.expenseId || expense.id;
      await expenseService.delete(expenseId);
      showNotification('Expense deleted successfully', 'success');
      fetchExpenses();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      alertNotification('Failed to delete expense', 'error');
    }
  };

  /**
   * Open edit modal with selected expense
   */
  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setIsEditModalOpen(true);
  };

  /**
   * Export expenses to CSV
   */
  const handleExportCSV = () => {
    if (expenses.length === 0) {
      alertNotification('No data to export', 'warning');
      return;
    }

    let csvContent = "ID,Type,Reason/Details,Amount\n";
    
    expenses.forEach(expense => {
      const row = [
        expense.expenseId || expense.id,
        expense.type,
        `"${expense.reason?.replace(/"/g, '""') || ''}"`,
        parseFloat(expense.amount).toFixed(2)
      ];
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    
    const today = new Date().toISOString().split('T')[0];
    a.download = `expenses_report_${today}.csv`;
    a.href = url;
    a.click();
    
    window.URL.revokeObjectURL(url);
    showNotification('CSV exported successfully', 'success');
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <main className="inventory-main-content">
      {/* Page Header */}
      <header className="inventory-content-header">
        <div className="header-left">
          <button
            onClick={toggleSidebar}
            className="sidebar-toggle-btn"
            type="button"
          >
            ☰
          </button>
          <h2 className="inventory-content-title">Expense Management</h2>
        </div>

        <div className="header-actions">
          <button
            className="inventory-btn-add"
            onClick={() => setIsAddModalOpen(true)}
            type="button"
          >
            <PlusCircle size={18} />
            Add Expense
          </button>
          <button
            className="inventory-btn-add"
            onClick={handleExportCSV}
            type="button"
            style={{ background: '#10B981' }}
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          <input
            type="text"
            name="search"
            className="inventory-custom-input inventory-search-input"
            placeholder="Search by reason or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="inventory-filter-container">
            <select
              className="inventory-filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="SALARY">Salary</option>
              <option value="ELECTRICITY">Electricity</option>
              <option value="FUEL">Fuel</option>
              <option value="RENT">Rent</option>
              <option value="REPAIR">Repair</option>
              <option value="MISC">Miscellaneous</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          <button
            className="inventory-btn-clear"
            onClick={handleClearFilters}
            type="button"
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* Data Table */}
      <section>
        <ExpenseTable
          expenses={expenses}
          onEdit={handleEditClick}
          onDelete={handleDeleteExpense}
          isLoading={isLoading}
        />
      </section>

      {/* Pagination Info */}
      {!isLoading && expenses.length > 0 && (
        <div className="inventory-pagination-info">
          Showing {expenses.length} expense(s) on page {currentPage}
        </div>
      )}

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button
          className="btn-page"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => prev - 1)}
          type="button"
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {currentPage > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {currentPage > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {currentPage > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {currentPage - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{currentPage}</button>
          
          {hasNextPage && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {currentPage + 1}
            </button>
          )}
          
          {hasNextPage && currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {currentPage} of {totalPages || 1}</span>

        <button
          className="btn-page"
          disabled={!hasNextPage}
          onClick={() => setCurrentPage((prev) => prev + 1)}
          type="button"
        >
          Next
        </button>
      </div>

      {/* Add Expense Modal (Batch) */}
      <ExpenseModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleCreateExpenses}
        isSaving={isSaving}
      />

      {/* Edit Expense Modal (Single) */}
      <EditExpenseModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedExpense(null);
        }}
        onSave={handleUpdateExpense}
        expense={selectedExpense}
        isSaving={isSaving}
      />
    </main>
  );
};

export default Expenses;